import { useCallback } from "react";

import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { ScoringBoard } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/scoring-board/ScoringBoard";
import { ScoringBoardError } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/scoring-board/ScoringBoardError";
import { ScoringBoardLoader } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/scoring-board/ScoringBoardLoader";
import { ScoringRuleDeleteModal } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/ScoringRuleDeleteModal";
import { ScoringRuleWizardModal } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/ScoringRuleWizardModal";
import useSaveRules from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useSaveRules";
import { useScoringBoardCrud } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringBoardCrud";
import { useScoringBoardData } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringBoardData";
import { useScoringBoardTrackingService } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringBoardTrackingService";
import { useScoringBoardModalState } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringBoardModalState";
import {
    prepareWizardRuleUpdate,
    reconcileSelectedRuleAfterDelete,
    resolveEffectiveSelectedRuleId,
    SCORING_PRIORITY_COLUMNS,
    removeRuleFromColumn,
    moveRuleBetweenColumns,
} from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/scoringBoardUtils";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import type { LeadPriorityScore } from "app/pages/sales-intelligence/modules/lists/types/columns";
import type {
    IScoringConfigDto,
    IScoringRuleDto,
    ScoringConfigType,
} from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";

type WizardState =
    | { mode: "create"; priority: LeadPriorityScore }
    | { mode: "edit"; priority: LeadPriorityScore; ruleIndex: number; rule: IScoringRuleDto };

type DeleteRuleState = { priority: LeadPriorityScore; ruleIndex: number };

type Props = {
    configType: ScoringConfigType;
    filtersList: readonly IFilterInstance<LeadsSearchAllFiltersTypes>[];
    isBootstrapping?: boolean;
    data?: IScoringConfigDto;
    isFetching?: boolean;
    selectedRuleId?: string | null;
    onSelect?: (ruleId: string) => void;
    getRuleUsage?: (rule: IScoringRuleDto) => readonly string[];
    automationPrefix?: string;
};

export const ScoringBoardContainer = ({
    configType,
    filtersList,
    isBootstrapping = false,
    data: dataOverride,
    isFetching: isFetchingOverride,
    selectedRuleId = null,
    onSelect,
    getRuleUsage,
    automationPrefix,
}: Props) => {
    const scoringBoardTracking = useScoringBoardTrackingService();
    const { saveRules, isPending: isSaving } = useSaveRules(configType);
    const { persist, persistOptimistic } = useScoringBoardCrud();
    const {
        data: fetchedData,
        rules: fetchedRules,
        signalById,
        isError,
        refetch,
        isInitialLoading,
        isFetching: fetchedIsFetching,
    } = useScoringBoardData(configType);

    const isSelfFetching = dataOverride === undefined;
    const data = dataOverride ?? fetchedData;
    const rules = data?.rules ?? fetchedRules;
    const isFetching = isFetchingOverride ?? fetchedIsFetching;
    const resolvedAutomationPrefix = automationPrefix ?? (onSelect ? "scoring-rule-selector" : "scoring-board");
    const effectiveSelectedRuleId = onSelect ? resolveEffectiveSelectedRuleId(rules, selectedRuleId) : undefined;

    const {
        wizardState,
        setWizardState,
        handleWizardClose,
        deleteState,
        setDeleteState,
        deleteError,
        clearDeleteError,
        resetDeleteState,
        confirmDeleteIfReady,
        handleDeleteModalClose,
        getDeletePersistOptions,
    } = useScoringBoardModalState<WizardState, DeleteRuleState>(scoringBoardTracking);

    const handleAddRule = (priority: LeadPriorityScore) => {
        scoringBoardTracking.trackAddRule(priority);
        setWizardState({ mode: "create", priority });
    };

    const handleEditRule = (priority: LeadPriorityScore, ruleIndex: number) => {
        const rule = rules[priority]?.[ruleIndex];
        if (!rule) {
            return;
        }
        scoringBoardTracking.trackOpenEditRule();
        setWizardState({ mode: "edit", priority, ruleIndex, rule });
    };

    const handleDeleteRequest = (priority: LeadPriorityScore, ruleIndex: number) => {
        scoringBoardTracking.trackRequestDeleteRule();
        clearDeleteError();
        setDeleteState({ priority, ruleIndex });
    };

    const handleDeleteConfirm = () => {
        const activeDeleteState = confirmDeleteIfReady();

        if (!activeDeleteState) {
            return;
        }

        clearDeleteError();

        const { priority, ruleIndex } = activeDeleteState;
        const { nextRules, deletedRuleId } = removeRuleFromColumn(rules, priority, ruleIndex);

        persist(
            saveRules,
            nextRules,
            "si.pages.score_settings.toast.rule_deleted",
            () => {
                if (onSelect) {
                    reconcileSelectedRuleAfterDelete(deletedRuleId, effectiveSelectedRuleId, nextRules, onSelect);
                }
                resetDeleteState();
            },
            getDeletePersistOptions(),
        );
    };

    const handleWizardSave = (rule: IScoringRuleDto) => {
        if (!wizardState) {
            return;
        }

        const { nextRules, successKey, isCreate } = prepareWizardRuleUpdate(rules, wizardState, rule);

        persist(saveRules, nextRules, successKey, () => {
            scoringBoardTracking.trackSaveRuleWizard(isCreate ? "create" : "edit");
            handleWizardClose();
            if (onSelect && isCreate && rule.id) {
                onSelect(rule.id);
            }
        });
    };

    const handleMoveRule = onSelect
        ? undefined
        : (sourcePriority: LeadPriorityScore, ruleIndex: number, targetPriority: LeadPriorityScore) => {
              const moveResult = moveRuleBetweenColumns(rules, sourcePriority, ruleIndex, targetPriority);

              if (!moveResult) {
                  return;
              }

              scoringBoardTracking.trackMoveRule(targetPriority);
              persistOptimistic(
                  saveRules,
                  moveResult.nextRules,
                  "si.pages.score_settings.toast.rule_moved",
                  () => undefined,
              );
          };

    const handleSelectRule = onSelect
        ? (ruleId: string) => {
              scoringBoardTracking.trackSelectRule();
              onSelect(ruleId);
          }
        : undefined;

    const showLoader = isSelfFetching && (isInitialLoading || isBootstrapping);

    const getRuleScope = useCallback(() => configType, [configType]);

    if (showLoader) {
        return <ScoringBoardLoader />;
    }

    if (isSelfFetching && isError) {
        return (
            <ScoringBoardError
                onRetry={() => {
                    scoringBoardTracking.trackBoardRetry();
                    void refetch();
                }}
                dataAutomation="scoring-board-retry"
            />
        );
    }

    return (
        <>
            <ScoringBoard
                columns={SCORING_PRIORITY_COLUMNS}
                rules={rules}
                filtersList={filtersList}
                signalById={signalById}
                onAddRule={handleAddRule}
                onEditRule={handleEditRule}
                onDeleteRule={handleDeleteRequest}
                onMoveRule={handleMoveRule}
                selectedRuleId={effectiveSelectedRuleId}
                onSelect={handleSelectRule}
                getRuleUsage={getRuleUsage}
                getRuleScope={getRuleScope}
                automationPrefix={resolvedAutomationPrefix}
            />

            {wizardState ? (
                <ScoringRuleWizardModal
                    isOpen
                    mode={wizardState.mode}
                    priority={wizardState.priority}
                    configType={configType}
                    initialRule={wizardState.mode === "edit" ? wizardState.rule : undefined}
                    onClose={handleWizardClose}
                    onSave={handleWizardSave}
                    isSaving={isSaving || isFetching}
                />
            ) : null}

            <ScoringRuleDeleteModal
                errorMessage={deleteError}
                isOpen={deleteState !== null}
                isSubmitLoading={isSaving}
                onClose={handleDeleteModalClose}
                onConfirm={handleDeleteConfirm}
            />
        </>
    );
};

export type ScoringBoardContainerProps = Props;
